import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import IconButtonTsoPq from '@components/Buttons/IconButtonTsoPq';
import FormFieldTsoPq from '@components/Form/FormFieldTsoPq';
import FormTsoPq from '@components/Form/FormTsoPq';
import ModalDialogTsoPq from '@components/ModalDialog/ModalDialogTsoPq';
import PermissionGate from '@components/PermissionGate/PermissionGate';
import { userOrSupportRestriction } from '@components/PermissionGate/RolePermissionConfig';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { Box, styled, Typography } from '@mui/material';
import {
  useGetSubstationByIdIsReferencedQuery,
  useGetSubstationByIdQuery,
  usePostSubstationMutation,
  usePutSubstationMutation,
} from '@redux/apiSlices/substationApi';
import { useAppDispatch } from '@redux/hooks';
import { setSnackbar } from '@redux/slices/snackbarSlice';
import { skipToken } from '@reduxjs/toolkit/query';
import { useRoleCheck } from '@utils/hooks/useRoleCheck';
import { commentSchema, latitudeSchema, longitudeSchema, mandatory, stringSchema } from '@utils/validationSchema';
import { Dispatch, ReactNode, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { object, z } from 'zod';

type SubstationProps = {
  newSubstation?: boolean;
  handleDiscard?: Dispatch<SetStateAction<boolean>>;
  refetch?: any;
  onDeleteConfirm?: () => void;
  onCancel?: () => void;
  deleteTitle?: string;
  deleteText?: string;
  onDiscard?: () => void;
};

const StyledPageWrapper = styled(Box, {
  shouldForwardProp: (prop) => !['newSubstation', 'setValue'].includes(prop as string),
})(({ newSubstation = false }: { newSubstation: boolean | undefined }) => {
  return {
    width: '47.125rem',
    padding: newSubstation ? '0' : '1rem 0rem 2rem 0rem',
  };
});

const StyledSubstationHeader = styled(Box, {
  shouldForwardProp: (prop) => !['newSubstation', 'setValue'].includes(prop as string),
})(({ newSubstation = false }: { newSubstation: boolean | undefined }) => {
  return {
    display: 'flex',
    padding: newSubstation
      ? `0 0 ${tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_24} 0`
      : `0 0 ${tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_4} 0`,
    gap: tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_page_gap_xxs,
  };
});

const StyledSubstationTitle = styled(Typography)(() => {
  return {
    margin: 'auto 0',
    color: tsoPqTheme.palette.customTextColor.color_font_ft_text,
    fontFamily: tsoPqTheme.typography.openSans,
    fontSize: tsoPqTheme.typography.fontsize_title_section_smaller,
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '2.125rem',
  };
});

const StyledSubstationSubtitle = styled(Typography)(() => {
  return {
    margin: 'auto 0',
    color: tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_enabled,
    fontFamily: tsoPqTheme.typography.openSans,
    fontSize: tsoPqTheme.typography.fontsize_body_body_medium,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '1.25rem',
    textDecoration: 'none',
    padding: ` 0 0 ${tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_24} 0`,
  };
});

const StyledSubstationWrapper = styled(Box)(() => {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_page_gap_xxs,
  };
});

const StyledSubstationLineWrapper = styled(Box)(() => {
  return {
    display: 'flex',
    gap: tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_page_gap_xxs,
  };
});

const FormContent = ({ register, errors }: { register: any; errors: any }): ReactNode => {
  const supportOrUserAllowed = useRoleCheck(userOrSupportRestriction);

  return (
    <StyledSubstationWrapper>
      <FormFieldTsoPq
        label="management.substation.designation"
        placeholder="management.substation.designation"
        name="name"
        type="text"
        errorMessage="general.error.characterLimit"
        required
        register={register}
        errors={errors}
        disabled={!supportOrUserAllowed}
      />
      <StyledSubstationLineWrapper>
        <FormFieldTsoPq
          label="management.substation.latitude"
          placeholder="management.substation.latitude"
          name="latitude"
          customWidth="9.125rem"
          type="text"
          errorMessage="management.substation.latitude.error"
          register={register}
          errors={errors}
          disabled={!supportOrUserAllowed}
        />
        <FormFieldTsoPq
          label="management.substation.longitude"
          placeholder="management.substation.longitude"
          name="longitude"
          customWidth="9.125rem"
          type="text"
          errorMessage="management.substation.longitude.error"
          register={register}
          errors={errors}
          disabled={!supportOrUserAllowed}
        />
      </StyledSubstationLineWrapper>
      <FormFieldTsoPq
        label="management.substation.comment"
        placeholder="management.substation.comment"
        name="comment"
        type="text"
        multiline
        customWidth="47.125rem"
        rows={4}
        errorMessage="general.error.characterLimit"
        register={register}
        errors={errors}
        disabled={!supportOrUserAllowed}
      />
    </StyledSubstationWrapper>
  );
};

const Substation = ({
  newSubstation,
  handleDiscard,
  refetch,
  onDeleteConfirm,
  onCancel,
  deleteTitle,
  deleteText,
  onDiscard,
}: SubstationProps) => {
  const dispatch = useAppDispatch();

  const { type, deviceId } = useParams();
  const { t } = useTranslation();
  const supportOrUserAllowed = useRoleCheck(userOrSupportRestriction);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data: substation } = useGetSubstationByIdQuery(
    type == 'umspannwerk' && deviceId ? Number(deviceId) : skipToken
  );

  const [addEntity] = usePostSubstationMutation();
  const [editEntity] = usePutSubstationMutation();

  const { data: isReferenced } = useGetSubstationByIdIsReferencedQuery(
    type == 'umspannwerk' && deviceId ? Number(deviceId) : skipToken
  );

  const handleFormSubmit = async (data: any) => {
    const substationData = { ...data };
    try {
      if (newSubstation) {
        await addEntity({ externalDto: { tsoId: 1, ...substationData } }).unwrap();
        await refetch();
        dispatch(
          setSnackbar({
            open: true,
            type: 'success',
            textContent: t('snackbar.substation.new.success'),
            copyToClipboard: false,
            requestID: undefined,
          })
        );
      } else {
        await editEntity({ externalDto: { ...substation?.externalDto, ...substationData } }).unwrap();
        await refetch();
        dispatch(
          setSnackbar({
            open: true,
            type: 'success',
            textContent: t('snackbar.substation.update.success'),
            copyToClipboard: false,
            requestID: undefined,
          })
        );
      }
      handleDiscard && handleDiscard(false);
    } catch (error: any) {
      console.error('Error saving substation:', error);
      const requestID = error?.data?.traceId || undefined;

      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: newSubstation ? t('snackbar.substation.new.error') : t('snackbar.substation.update.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    }
  };

  const validationSchema = object({
    name: mandatory.and(stringSchema),
    latitude: z.preprocess((val) => (val === '' || val == null ? undefined : val), latitudeSchema.optional()),
    longitude: z.preprocess((val) => (val === '' || val == null ? undefined : val), longitudeSchema.optional()),
    comment: commentSchema,
  });

  return (
    (deviceId || newSubstation) && (
      <StyledPageWrapper newSubstation={newSubstation}>
        <StyledSubstationHeader newSubstation={newSubstation}>
          <StyledSubstationTitle>
            {(newSubstation && t('management.substation.create.title')) || substation?.externalDto?.name}
          </StyledSubstationTitle>
          {!newSubstation && (
            <PermissionGate roles={userOrSupportRestriction}>
              <IconButtonTsoPq
                sizeVariant={'l'}
                tooltip={
                  isReferenced
                    ? t('management.measurementPoint.substationCannotDelete')
                    : t('management.measurementPoint.substationDeleteTitle')
                }
                buttonType="transparentPrimaryNoEffect"
                disabled={isReferenced}
                icon={faTrash}
                onClick={(event) => {
                  event.stopPropagation();
                  setShowDeleteModal(true);
                }}
              />
            </PermissionGate>
          )}
        </StyledSubstationHeader>
        {!newSubstation && <StyledSubstationSubtitle>{t('management.substation.subtitle')}</StyledSubstationSubtitle>}
        <FormTsoPq
          newForm={newSubstation}
          onSubmit={handleFormSubmit}
          discardChanges={handleDiscard}
          defaultValues={{}}
          values={newSubstation ? {} : (substation?.externalDto ?? {})}
          schema={validationSchema}
          withoutDiscard={!supportOrUserAllowed}
          hideSaveButton={!supportOrUserAllowed}
        >
          {FormContent}
        </FormTsoPq>
        {showDeleteModal && (
          <ModalDialogTsoPq
            modalState={showDeleteModal}
            title={deleteTitle ?? ''}
            deleteConfirmationModal
            confirmationText={deleteText ?? ''}
            onConfirm={() => {
              onDeleteConfirm?.();
              setShowDeleteModal(false);
              onDiscard?.();
            }}
            onCancel={() => {
              onCancel?.();
              setShowDeleteModal(false);
            }}
            onClose={() => setShowDeleteModal(false)}
          />
        )}
      </StyledPageWrapper>
    )
  );
};

export default Substation;
